import { useVendorStore } from '../../stores/vendorStore';
import { UploadInput } from '../UploadInput';
import { uploadBankAttachment, updateBank } from '../../services/api';
import { prepareUploadDataUrl } from '../../utils/imageUpload';
import { toast } from 'sonner';

const FIELD_TO_ATTR = {
  aadhaar: 'aadhaarDataUrl',
  pan: 'panDataUrl',
  debitCard: 'debitCardImageDataUrl',
  creditCard: 'creditCardImageDataUrl',
  openingScreenshot: 'openingScreenshotDataUrl',
  openingStatement: 'openingStatementDataUrl',
  qr: 'qrDataUrl',
  freezeAttachment: 'freezeAttachmentDataUrl',
};

const isNetworkOrMissingEndpointError = (err) => {
  if (!err) return false;
  // Network error (no response received) OR server doesn't have the new route.
  if (!err.response) return true;
  return err.response.status === 404 || err.response.status === 405 || err.response.status === 502;
};

/**
 * Attachment uploader for the selected bank. Tries multipart first (production-
 * safe, large-file friendly). If the new endpoint is unavailable (older backend
 * deployment, gateway issue), transparently falls back to compressing client-
 * side and patching via the existing JSON `updateBank` endpoint so the user
 * never sees an avoidable failure.
 */
export const BankAttachmentUpload = ({
  field,
  cardIndex,
  accept,
  allowPdf = true,
  className = '',
  onUploaded,
  ...rest
}) => {
  const vendorId = useVendorStore((s) => s.selectedVendor?.id);
  const bankId = useVendorStore((s) => s.selectedBank?.id);

  const handleFile = async (file) => {
    if (!vendorId || !bankId) {
      toast.error('No bank selected');
      throw new Error('No bank selected');
    }

    // 1) Multipart path (preferred).
    try {
      const res = await uploadBankAttachment(vendorId, bankId, field, file, { cardIndex });
      useVendorStore.setState({ selectedBank: res.data });
      await useVendorStore.getState().fetchVendors();
      if (onUploaded) onUploaded(res.data);
      return;
    } catch (err) {
      if (!isNetworkOrMissingEndpointError(err)) {
        toast.error(err?.response?.data?.detail || `Failed to upload ${field}`);
        throw err;
      }
      // Continue to fallback.
    }

    // 2) JSON base64 fallback — compress client-side, PATCH via updateBank.
    try {
      const { dataUrl } = await prepareUploadDataUrl(file, { allowPdf });
      const attr = FIELD_TO_ATTR[field];
      if (!attr) throw new Error(`Unsupported field: ${field}`);
      const payload = { [attr]: dataUrl };
      // Per-card debit card image: write into the array too.
      if (field === 'debitCard' && cardIndex >= 0) {
        const bank = useVendorStore.getState().selectedBank;
        const cards = (bank?.debitCards || []).map((c, i) =>
          i === cardIndex ? { ...c, imageDataUrl: dataUrl } : c
        );
        payload.debitCards = cards;
      }
      const res = await updateBank(vendorId, bankId, payload);
      useVendorStore.setState({ selectedBank: res.data });
      await useVendorStore.getState().fetchVendors();
      if (onUploaded) onUploaded(res.data);
      toast.info('Uploaded via fallback path');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || `Failed to upload ${field}`);
      throw err;
    }
  };

  return (
    <UploadInput
      className={className}
      accept={accept || 'image/*'}
      allowPdf={allowPdf}
      onFileUpload={handleFile}
      {...rest}
    />
  );
};

export default BankAttachmentUpload;
